import { lazy, type ComponentType } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { PERMISSIONS } from '@hrms/shared';
import { AppLayout } from '@/components/layout/app-layout';
import { ChangePasswordPage } from '@/features/auth/change-password-page';
import { ForgotPasswordPage } from '@/features/auth/forgot-password-page';
import { LoginPage } from '@/features/auth/login-page';
import { ResetPasswordPage } from '@/features/auth/reset-password-page';
import { DashboardPage } from '@/features/dashboard/dashboard-page';
import { TermsPage } from '@/features/legal/terms-page';
import { ForbiddenPage, NotFoundPage } from '@/features/misc/status-pages';
import { RequireAnonymous, RequireAuth, RequirePermission } from './guards';

/**
 * Loads a page's module the first time somebody navigates to it.
 *
 * Every page here is a named export, so the component is picked out of the
 * module after the chunk lands — `React.lazy` insists on a default export.
 *
 * What this buys: the whole application used to arrive in one 1.17 MB script,
 * so an employee who signs in to clock in and read a payslip still downloaded
 * the vehicle register, the contribution tables and every finance screen before
 * the dashboard could paint. Login and the dashboard stay eager below, because
 * splitting the first screen only adds a round-trip to it.
 */
function page<K extends string>(name: K, load: () => Promise<Record<K, ComponentType>>) {
  return lazy(async () => ({ default: (await load())[name] }));
}

const EmployeeDetailPage = page(
  'EmployeeDetailPage',
  () => import('@/features/employees/employee-detail-page'),
);
const EmployeeFormPage = page(
  'EmployeeFormPage',
  () => import('@/features/employees/employee-form-page'),
);
const EmployeeHistoryPage = page(
  'EmployeeHistoryPage',
  () => import('@/features/employees/employee-history-page'),
);
const EmployeesPage = page('EmployeesPage', () => import('@/features/employees/employees-page'));
const OrgChartPage = page('OrgChartPage', () => import('@/features/employees/org-chart-page'));
const AttendancePage = page(
  'AttendancePage',
  () => import('@/features/attendance/attendance-page'),
);
const MyDevicesPage = page('MyDevicesPage', () => import('@/features/location/my-devices-page'));
const TeamLocationPage = page(
  'TeamLocationPage',
  () => import('@/features/location/team-location-page'),
);
const UispPage = page('UispPage', () => import('@/features/integrations/uisp-page'));
const ChatPage = page('ChatPage', () => import('@/features/chat/chat-page'));
const ChatNamesPage = page('ChatNamesPage', () => import('@/features/chat/chat-names-page'));
const LeavePage = page('LeavePage', () => import('@/features/leave/leave-page'));
const LeaveEntitlementsPage = page(
  'LeaveEntitlementsPage',
  () => import('@/features/leave/leave-entitlements-page'),
);
const RestDayWorkPage = page(
  'RestDayWorkPage',
  () => import('@/features/attendance/rest-day-work-page'),
);
const WorkSchedulesPage = page(
  'WorkSchedulesPage',
  () => import('@/features/attendance/work-schedules-page'),
);
const ExpenseCategoriesPage = page(
  'ExpenseCategoriesPage',
  () => import('@/features/finance/expense-categories-page'),
);
const FundApprovalsPage = page(
  'FundApprovalsPage',
  () => import('@/features/finance/fund-approvals-page'),
);
const FundRequestsPage = page(
  'FundRequestsPage',
  () => import('@/features/finance/fund-requests-page'),
);
const FundReleasePage = page(
  'FundReleasePage',
  () => import('@/features/finance/fund-release-page'),
);
const FundAccountabilityPage = page(
  'FundAccountabilityPage',
  () => import('@/features/finance/fund-accountability-page'),
);
const LiquidationReviewPage = page(
  'LiquidationReviewPage',
  () => import('@/features/finance/liquidation-review-page'),
);
const FundSettlementPage = page(
  'FundSettlementPage',
  () => import('@/features/finance/fund-settlement-page'),
);
const ExpenseReportPage = page(
  'ExpenseReportPage',
  () => import('@/features/finance/expense-report-page'),
);
const FuelLogsPage = page('FuelLogsPage', () => import('@/features/finance/fuel-logs-page'));
const ExpenseClaimsPage = page(
  'ExpenseClaimsPage',
  () => import('@/features/finance/expense-claims-page'),
);
const BudgetsPage = page('BudgetsPage', () => import('@/features/finance/budgets-page'));
const VehiclesPage = page('VehiclesPage', () => import('@/features/finance/vehicles-page'));
const ExpenseClaimReviewPage = page(
  'ExpenseClaimReviewPage',
  () => import('@/features/finance/expense-claim-review-page'),
);
const PermissionsPage = page('PermissionsPage', () => import('@/features/users/permissions-page'));
const UsersPage = page('UsersPage', () => import('@/features/users/users-page'));
const AnalyticsPage = page('AnalyticsPage', () => import('@/features/analytics/analytics-page'));
const AnnouncementsPage = page(
  'AnnouncementsPage',
  () => import('@/features/announcements/announcements-page'),
);
const AwardsPage = page('AwardsPage', () => import('@/features/awards/awards-page'));
const DisciplinaryPage = page(
  'DisciplinaryPage',
  () => import('@/features/disciplinary/disciplinary-page'),
);
const DocumentsPage = page('DocumentsPage', () => import('@/features/documents/documents-page'));
const InsurancePage = page('InsurancePage', () => import('@/features/insurance/insurance-page'));
const PerformancePage = page(
  'PerformancePage',
  () => import('@/features/performance/performance-page'),
);
const ReferenceDataPage = page(
  'ReferenceDataPage',
  () => import('@/features/reference/reference-data-page'),
);
const ReportsPage = page('ReportsPage', () => import('@/features/reports/reports-page'));
const TrainingPage = page('TrainingPage', () => import('@/features/training/training-page'));
const RequestsPage = page('RequestsPage', () => import('@/features/requests/requests-page'));
const ResignationPage = page(
  'ResignationPage',
  () => import('@/features/resignation/resignation-page'),
);
const AssetsPage = page('AssetsPage', () => import('@/features/assets/assets-page'));
const MyAssetsPage = page('MyAssetsPage', () => import('@/features/assets/my-assets-page'));
const AuditLogPage = page('AuditLogPage', () => import('@/features/audit/audit-log-page'));
const HolidaysPage = page('HolidaysPage', () => import('@/features/holidays/holidays-page'));
const SettingsPage = page('SettingsPage', () => import('@/features/settings/settings-page'));
const ContributionTablesPage = page(
  'ContributionTablesPage',
  () => import('@/features/payroll/contribution-tables-page'),
);
const MyPayslipsPage = page('MyPayslipsPage', () => import('@/features/payroll/my-payslips-page'));
const PayrollPage = page('PayrollPage', () => import('@/features/payroll/payroll-page'));
const ThirteenthMonthPage = page(
  'ThirteenthMonthPage',
  () => import('@/features/payroll/thirteenth-month-page'),
);
const PayrollRunPage = page('PayrollRunPage', () => import('@/features/payroll/payroll-run-page'));
const DepartmentsPage = page(
  'DepartmentsPage',
  () => import('@/features/organisation/departments-page'),
);
const PositionsPage = page('PositionsPage', () => import('@/features/organisation/positions-page'));
const SalaryGradesPage = page(
  'SalaryGradesPage',
  () => import('@/features/organisation/salary-grades-page'),
);

/**
 * Route table.
 *
 * Declared with `<Routes>` rather than a data router: the module routes landing
 * in later steps are permission-gated, and expressing that as nested guard
 * elements is clearer than threading `loader` checks through a route object
 * tree.
 *
 * Every page but the first screens is lazy — see `page` above. The `Suspense`
 * boundary that covers them lives in AppLayout, around the outlet, so a chunk
 * still arriving swaps the content area and leaves the chrome where it is.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route element={<RequireAnonymous />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
      </Route>

      <Route element={<RequireAuth />}>
        <Route element={<AppLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          {/* Reachable while the gate blocks everything else. */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/forbidden" element={<ForbiddenPage />} />

          <Route
            element={
              <RequirePermission
                anyOf={[PERMISSIONS.EMPLOYEE_READ_ALL, PERMISSIONS.EMPLOYEE_READ_TEAM]}
              />
            }
          >
            <Route path="/employees" element={<EmployeesPage />} />
            {/* Declared before `:id` — otherwise "new" and "<id>/edit" are
                matched as employee ids and the detail page 400s on them. */}
            <Route element={<RequirePermission anyOf={[PERMISSIONS.EMPLOYEE_CREATE]} />}>
              <Route path="/employees/new" element={<EmployeeFormPage />} />
            </Route>
            <Route element={<RequirePermission anyOf={[PERMISSIONS.EMPLOYEE_UPDATE_ALL]} />}>
              <Route path="/employees/:id/edit" element={<EmployeeFormPage />} />
            </Route>
            <Route path="/employees/:id" element={<EmployeeDetailPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.DEPARTMENT_READ]} />}>
            <Route path="/departments" element={<DepartmentsPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.POSITION_READ]} />}>
            <Route path="/positions" element={<PositionsPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.SALARY_GRADE_READ]} />}>
            <Route path="/salary-grades" element={<SalaryGradesPage />} />
          </Route>

          <Route
            element={
              <RequirePermission
                anyOf={[PERMISSIONS.EMPLOYEE_HISTORY_READ_ALL, PERMISSIONS.EMPLOYEE_HISTORY_MANAGE]}
              />
            }
          >
            <Route path="/employee-history" element={<EmployeeHistoryPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.ORG_CHART_VIEW]} />}>
            <Route path="/org-chart" element={<OrgChartPage />} />
          </Route>

          <Route
            element={
              <RequirePermission
                anyOf={[
                  PERMISSIONS.ATTENDANCE_READ_OWN,
                  PERMISSIONS.ATTENDANCE_READ_TEAM,
                  PERMISSIONS.ATTENDANCE_READ_ALL,
                ]}
              />
            }
          >
            <Route path="/attendance" element={<AttendancePage />} />
          </Route>

          {/* Its own module, and its own grants — holding attendance:read:all
              says nothing about whether somebody may see where people are. */}
          <Route
            element={
              <RequirePermission
                anyOf={[PERMISSIONS.LOCATION_READ_TEAM, PERMISSIONS.LOCATION_READ_ALL]}
              />
            }
          >
            <Route path="/location" element={<TeamLocationPage />} />
          </Route>

          {/* Gated on the permission that lets somebody report at all: if you can
              register a phone, you can see and stop the ones you registered. */}
          <Route element={<RequirePermission anyOf={[PERMISSIONS.LOCATION_SHARE]} />}>
            <Route path="/my-devices" element={<MyDevicesPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.UISP_OPEN]} />}>
            <Route path="/uisp" element={<UispPage />} />
          </Route>

          {/*
            Deliberately outside every RequirePermission wrapper. Reading your
            own payslip is not a grant anyone should have to be given, and the
            endpoint behind it is scoped to the person asking. This is also
            where the payslip email lands.
          */}
          <Route path="/payroll/payslips" element={<MyPayslipsPage />} />
          <Route path="/payroll/payslips/:id" element={<MyPayslipsPage />} />

          {/*
            Also outside every RequirePermission wrapper. Chat is for everybody
            who works here; which conversations a person may open is decided by
            membership on the server, and whether chat is on at all is a
            company switch the page reads from the server's answer.
          */}
          <Route path="/chat" element={<ChatPage />} />
          {/* Before the :threadId route, so "names" is never read as an id. */}
          <Route element={<RequirePermission anyOf={[PERMISSIONS.CHAT_MANAGE]} />}>
            <Route path="/chat/names" element={<ChatNamesPage />} />
          </Route>
          <Route path="/chat/:threadId" element={<ChatPage />} />

          <Route
            element={
              <RequirePermission
                anyOf={[
                  PERMISSIONS.LEAVE_READ_OWN,
                  PERMISSIONS.LEAVE_READ_TEAM,
                  PERMISSIONS.LEAVE_READ_ALL,
                ]}
              />
            }
          >
            <Route path="/leave" element={<LeavePage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.LEAVE_MANAGE_BALANCES]} />}>
            <Route path="/leave/entitlements" element={<LeaveEntitlementsPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.WORK_SCHEDULE_READ]} />}>
            <Route path="/work-schedules" element={<WorkSchedulesPage />} />
          </Route>

          {/*
            Readable by anyone who can file a request, because the page explains
            what each category means; only an administrator can change them, and
            the write controls are hidden accordingly.
          */}
          <Route
            element={
              <RequirePermission
                anyOf={[PERMISSIONS.FUND_REQUEST_SUBMIT, PERMISSIONS.FINANCE_SETTINGS_MANAGE]}
              />
            }
          >
            <Route path="/finance/expense-categories" element={<ExpenseCategoriesPage />} />
          </Route>

          {/*
            Readable by anyone who can pick a vehicle on a request — an empty
            picker with no way to see why is what this page exists to end.
          */}
          <Route element={<RequirePermission anyOf={[PERMISSIONS.VEHICLE_READ]} />}>
            <Route path="/finance/vehicles" element={<VehiclesPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.FUND_REQUEST_READ_OWN]} />}>
            <Route path="/finance/fund-requests" element={<FundRequestsPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.EXPENSE_SUBMIT]} />}>
            <Route path="/finance/expense-claims" element={<ExpenseClaimsPage />} />
          </Route>

          {/*
            Approvers and payers share one screen; each section inside renders
            only for the permission that can act on it.
          */}
          <Route
            element={
              <RequirePermission
                anyOf={[
                  PERMISSIONS.FUND_REQUEST_APPROVE_MANAGER,
                  PERMISSIONS.FUND_REQUEST_APPROVE_HR,
                  PERMISSIONS.FUND_REQUEST_APPROVE_MANAGEMENT,
                  PERMISSIONS.REIMBURSEMENT_PAY,
                ]}
              />
            }
          >
            <Route path="/finance/expense-claims/approvals" element={<ExpenseClaimReviewPage />} />
          </Route>

          {/*
            Any rung gets in. The list itself is decided server-side from which
            levels the caller can act at, so a manager and an admin see the same
            page holding different requests.
          */}
          <Route
            element={
              <RequirePermission
                anyOf={[
                  PERMISSIONS.FUND_REQUEST_APPROVE_MANAGER,
                  PERMISSIONS.FUND_REQUEST_APPROVE_HR,
                  PERMISSIONS.FUND_REQUEST_APPROVE_MANAGEMENT,
                ]}
              />
            }
          >
            <Route path="/finance/approvals" element={<FundApprovalsPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.FUND_RELEASE]} />}>
            <Route path="/finance/release" element={<FundReleasePage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.LIQUIDATION_VERIFY]} />}>
            <Route path="/finance/liquidations" element={<LiquidationReviewPage />} />
          </Route>

          <Route
            element={
              <RequirePermission
                anyOf={[PERMISSIONS.FUND_RELEASE, PERMISSIONS.REIMBURSEMENT_PAY]}
              />
            }
          >
            <Route path="/finance/settlements" element={<FundSettlementPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.FINANCE_REPORT_VIEW]} />}>
            <Route path="/finance/accountability" element={<FundAccountabilityPage />} />
            <Route path="/finance/expense-report" element={<ExpenseReportPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.BUDGET_READ]} />}>
            <Route path="/finance/budgets" element={<BudgetsPage />} />
          </Route>

          {/*
            Everyone who can log a fill gets in — the driver holding the receipt
            is the one who knows the odometer reading. The fleet summary inside
            is gated separately on fuel:read:all.
          */}
          <Route
            element={
              <RequirePermission anyOf={[PERMISSIONS.FUEL_SUBMIT, PERMISSIONS.FUEL_READ_ALL]} />
            }
          >
            <Route path="/finance/fuel" element={<FuelLogsPage />} />
          </Route>

          {/*
            Payroll can reach the exception list without holding the authorising
            permission — whoever clears a blocked run has to be able to see what
            is blocking it, even if somebody else has to authorise it.
          */}
          <Route
            element={
              <RequirePermission
                anyOf={[PERMISSIONS.ATTENDANCE_AUTHORISE_REST_DAY, PERMISSIONS.PAYROLL_PROCESS]}
              />
            }
          >
            <Route path="/rest-day-work" element={<RestDayWorkPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.HOLIDAY_READ]} />}>
            <Route path="/holidays" element={<HolidaysPage />} />
          </Route>

          <Route
            element={
              <RequirePermission anyOf={[PERMISSIONS.ASSET_READ_ALL, PERMISSIONS.ASSET_MANAGE]} />
            }
          >
            <Route path="/assets" element={<AssetsPage />} />
          </Route>

          {/*
            Separate from /assets, and on the weaker grant: reading what you
            personally hold is not the same as reading the register. EMPLOYEE
            holds asset:read:own and nothing else here.
          */}
          <Route
            element={
              <RequirePermission anyOf={[PERMISSIONS.ASSET_READ_OWN, PERMISSIONS.ASSET_READ_ALL]} />
            }
          >
            <Route path="/assets/mine" element={<MyAssetsPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.ANNOUNCEMENT_READ]} />}>
            <Route path="/announcements" element={<AnnouncementsPage />} />
          </Route>

          <Route
            element={
              <RequirePermission
                anyOf={[
                  PERMISSIONS.REQUEST_READ_OWN,
                  PERMISSIONS.REQUEST_READ_TEAM,
                  PERMISSIONS.REQUEST_READ_ALL,
                ]}
              />
            }
          >
            <Route path="/requests" element={<RequestsPage />} />
          </Route>

          <Route
            element={
              <RequirePermission
                anyOf={[
                  PERMISSIONS.DISCIPLINARY_READ_OWN,
                  PERMISSIONS.DISCIPLINARY_READ_ALL,
                  PERMISSIONS.DISCIPLINARY_MANAGE,
                ]}
              />
            }
          >
            <Route path="/disciplinary" element={<DisciplinaryPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.AWARD_READ]} />}>
            <Route path="/awards" element={<AwardsPage />} />
          </Route>

          <Route
            element={
              <RequirePermission
                anyOf={[
                  PERMISSIONS.DOCUMENT_READ_OWN,
                  PERMISSIONS.DOCUMENT_READ_ALL,
                  PERMISSIONS.DOCUMENT_MANAGE,
                ]}
              />
            }
          >
            <Route path="/documents" element={<DocumentsPage />} />
          </Route>

          <Route
            element={
              <RequirePermission
                anyOf={[
                  PERMISSIONS.RESIGNATION_READ_OWN,
                  PERMISSIONS.RESIGNATION_READ_ALL,
                  PERMISSIONS.RESIGNATION_MANAGE,
                ]}
              />
            }
          >
            <Route path="/resignation" element={<ResignationPage />} />
          </Route>

          <Route
            element={
              <RequirePermission
                anyOf={[
                  PERMISSIONS.INSURANCE_READ_OWN,
                  PERMISSIONS.INSURANCE_READ_ALL,
                  PERMISSIONS.INSURANCE_MANAGE,
                ]}
              />
            }
          >
            <Route path="/insurance" element={<InsurancePage />} />
          </Route>

          <Route
            element={
              <RequirePermission
                anyOf={[
                  PERMISSIONS.TRAINING_READ_OWN,
                  PERMISSIONS.TRAINING_READ_ALL,
                  PERMISSIONS.TRAINING_MANAGE,
                ]}
              />
            }
          >
            <Route path="/training" element={<TrainingPage />} />
          </Route>

          <Route
            element={
              <RequirePermission
                anyOf={[
                  PERMISSIONS.PERFORMANCE_READ_OWN,
                  PERMISSIONS.PERFORMANCE_READ_TEAM,
                  PERMISSIONS.PERFORMANCE_READ_ALL,
                  PERMISSIONS.PERFORMANCE_MANAGE,
                ]}
              />
            }
          >
            <Route path="/performance" element={<PerformancePage />} />
          </Route>

          {/* An approver holds `payroll:approve` without `payroll:process`, and
              still has to open the run to sign it off. */}
          <Route
            element={
              <RequirePermission
                anyOf={[PERMISSIONS.PAYROLL_PROCESS, PERMISSIONS.PAYROLL_APPROVE]}
              />
            }
          >
            <Route path="/payroll" element={<PayrollPage />} />
            <Route path="/payroll/runs/:id" element={<PayrollRunPage />} />
            <Route path="/payroll/thirteenth-month" element={<ThirteenthMonthPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.PAYROLL_MANAGE_CONTRIBUTIONS]} />}>
            <Route path="/payroll/contribution-tables" element={<ContributionTablesPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.USER_READ]} />}>
            <Route path="/users" element={<UsersPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.PERMISSION_MANAGE]} />}>
            <Route path="/permissions" element={<PermissionsPage />} />
          </Route>

          <Route
            element={
              <RequirePermission anyOf={[PERMISSIONS.SETTING_READ, PERMISSIONS.SETTING_MANAGE]} />
            }
          >
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.AUDIT_LOG_READ]} />}>
            <Route path="/audit-log" element={<AuditLogPage />} />
          </Route>

          <Route
            element={
              <RequirePermission anyOf={[PERMISSIONS.REPORT_VIEW, PERMISSIONS.REPORT_EXPORT]} />
            }
          >
            <Route path="/reports" element={<ReportsPage />} />
          </Route>

          <Route element={<RequirePermission anyOf={[PERMISSIONS.ANALYTICS_VIEW]} />}>
            <Route path="/analytics" element={<AnalyticsPage />} />
          </Route>

          {/* Any one of these manage permissions gives at least one section;
              the page itself hides the rest. */}
          <Route
            element={
              <RequirePermission
                anyOf={[
                  PERMISSIONS.AWARD_MANAGE,
                  PERMISSIONS.TRAINING_MANAGE,
                  PERMISSIONS.INSURANCE_MANAGE,
                  PERMISSIONS.PERFORMANCE_MANAGE,
                  PERMISSIONS.REQUEST_PROCESS,
                  PERMISSIONS.ASSET_MANAGE,
                ]}
              />
            }
          >
            <Route path="/reference-data" element={<ReferenceDataPage />} />
          </Route>

          {/*
            Remaining module routes are added here as each lands, wrapped in
            <RequirePermission anyOf={[…]}> where the sidebar declares one.
            Until then their nav entries are marked `planned` and any direct
            navigation falls through to the 404 below.
          */}

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>

      {/* Reached only before the auth guards resolve. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
